import { useNavigate } from "react-router-dom";
import { Headphones, Play, Clock, Lock } from "lucide-react";
import { LIBRARY_PRACTICES } from "../lib/libraryPractices";
import { TRIAL_ACCESS_IDS, isLockedForUser, isTrialExpired } from "../lib/trialAccess";
import { useBilling } from "../contexts/BillingContext";

// Deterministic per-day pick: rotates through the three guided meditations.
// Uses days-since-epoch so every device shows the same meditation today.
const MEDITATION_SLUGS = [
  "mindfulness-of-breath",
  "mindful-body-scan",
  "mindfulness-of-inner-sensations",
];

// Returns the meditation to feature today. When `restrictToIds` is set
// (free-trial users), we ONLY rotate through those — so the dashboard hero
// never advertises a meditation the user can't actually open.
//
// Rotation is keyed to the user's *local* calendar day (not UTC), so the
// pick flips when the user's date flips — not at some odd hour determined
// by their timezone offset from UTC.
const pickTodaysMeditation = (restrictToIds = null) => {
  const allMeditations = LIBRARY_PRACTICES.filter((p) =>
    MEDITATION_SLUGS.includes(p.id)
  );
  if (allMeditations.length === 0) return null;
  const ordered = MEDITATION_SLUGS.map((slug) =>
    allMeditations.find((m) => m.id === slug)
  ).filter(Boolean);
  const pool = restrictToIds
    ? ordered.filter((m) => restrictToIds.has(m.id))
    : ordered;
  if (pool.length === 0) return null;
  // Days-since-epoch in the user's *local* timezone. Subtracting the
  // timezone offset (which is in minutes, positive when behind UTC)
  // before dividing flips the bucket boundary to local midnight.
  const now = new Date();
  const localMs = now.getTime() - now.getTimezoneOffset() * 60_000;
  const dayIndex = Math.floor(localMs / 86_400_000);
  return pool[dayIndex % pool.length];
};

export const DailyMeditationCard = () => {
  const navigate = useNavigate();
  // Billing status comes from the shared BillingContext (one fetch at
  // sign-in) so this card doesn't duplicate the request.
  const { billing } = useBilling();
  const billingStatus = billing?.status || null;

  const isTrialingActive = billingStatus === "trialing";
  const expired = isTrialExpired(billingStatus);
  // During an active trial, rotate through the trial whitelist. For expired
  // (or no billing) we still pick from the same trial whitelist so the card
  // has something coherent to show — but it renders as locked and routes to
  // /pricing instead of opening the meditation.
  const todays = pickTodaysMeditation(
    isTrialingActive || expired ? TRIAL_ACCESS_IDS : null
  );
  if (!todays) return null;

  const locked = isLockedForUser(todays.id, billingStatus);
  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <button
      onClick={() =>
        locked ? navigate("/pricing") : navigate(`/library/${todays.id}`)
      }
      data-testid="daily-meditation-card"
      className="group relative w-full mb-10 rounded-3xl overflow-hidden shadow-[0_8px_30px_rgba(27,35,73,0.08)] hover:shadow-[0_14px_42px_rgba(27,35,73,0.14)] transition-all duration-500 text-left"
    >
      {/* Hero image — uses today's meditation's own library tile image so the
          dashboard hero visually matches the practice tile it links to. */}
      <div
        className="relative h-56 sm:h-64 md:h-72 w-full bg-cover bg-center transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
        style={{
          backgroundImage: `url('${todays.image}')`,
          backgroundPosition: todays.imagePosition || "center",
        }}
      >
        {/* Soft bottom gradient so text stays legible */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e1a33]/85 via-[#0e1a33]/30 to-transparent" />

        {/* Top-left eyebrow */}
        <div className="absolute top-5 left-5 sm:top-6 sm:left-7 flex items-center gap-2">
          <span
            className="px-3 py-1 rounded-full text-[11px] font-medium uppercase tracking-[0.18em] backdrop-blur-md bg-white/20 text-white border border-white/30"
            data-testid="daily-meditation-eyebrow"
          >
            <Headphones className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
            Daily Meditation
          </span>
          {locked && (
            <span
              className="px-3 py-1 rounded-full text-[11px] font-medium uppercase tracking-[0.18em] backdrop-blur-md bg-amber-500/30 text-white border border-amber-300/40 inline-flex items-center gap-1.5"
              data-testid="daily-meditation-lock-pill"
            >
              <Lock className="w-3 h-3" />
              Paid
            </span>
          )}
        </div>

        {/* Bottom content */}
        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-7">
          <p className="text-white/80 text-xs sm:text-sm mb-2 tracking-wide">
            {today}
          </p>
          <h2
            className="font-outfit font-semibold text-white text-2xl sm:text-3xl md:text-4xl tracking-tight leading-tight mb-3 max-w-2xl"
            data-testid="daily-meditation-title"
          >
            {todays.title}
          </h2>
          <div className="flex items-center gap-4 text-white/85 text-sm">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {todays.duration}
            </span>
            <span className="opacity-60">·</span>
            <span>{todays.category}</span>
            <span
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#1B2349] font-medium text-sm shadow-lg group-hover:bg-[#3090A8] group-hover:text-white transition-colors"
              data-testid="daily-meditation-cta"
            >
              {locked ? (
                <>
                  <Lock className="w-4 h-4" />
                  <span className="hidden sm:inline">Unlock</span>
                  <span className="sm:hidden">Unlock</span>
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  <span className="hidden sm:inline">Listen now</span>
                  <span className="sm:hidden">Play</span>
                </>
              )}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
};

export default DailyMeditationCard;
